import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type { Carpeta, Documento, TipoDocumento } from './models/documental.model';

const KEY_CARPETAS = 'documental_carpetas';
const KEY_DOCUMENTOS = 'documental_documentos';
import {
  TIPO_DOCUMENTO_LABEL,
  TIPOS_DOCUMENTO,
  isDocumentoVencido,
  isDocumentoPorVencer,
} from './models/documental.model';

const CARPETAS_INICIALES: Carpeta[] = [
  { id: 'root', nombre: 'Raíz', padreId: null, orden: 0 },
  { id: '1', nombre: 'Políticas SST', padreId: 'root', orden: 1 },
  { id: '2', nombre: 'Procedimientos', padreId: 'root', orden: 2 },
  { id: '3', nombre: 'Certificados', padreId: 'root', orden: 3 },
  { id: '4', nombre: 'Capacitaciones', padreId: 'root', orden: 4 },
  { id: '5', nombre: 'Licencias', padreId: '3', orden: 1 },
];

function demoDocumentos(): Documento[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const next = (daysFromNow: number) =>
    new Date(Date.now() + daysFromNow * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      nombre: 'Política de SST',
      nombreArchivo: 'politica-sst.pdf',
      carpetaId: '1',
      tipoDocumento: 'politica',
      fechaSubida: d(90),
      fechaVencimiento: next(275),
      version: 2,
      contentType: 'application/pdf',
      tamanoBytes: 245000,
      observaciones: 'Versión actualizada',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      nombre: 'Procedimiento evacuación',
      nombreArchivo: 'proc-evacuacion.pdf',
      carpetaId: '2',
      tipoDocumento: 'procedimiento',
      fechaSubida: d(60),
      fechaVencimiento: next(305),
      version: 1,
      contentType: 'application/pdf',
      tamanoBytes: 180000,
      observaciones: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '3',
      nombre: 'Certificado bomberos',
      nombreArchivo: 'cert-bomberos.pdf',
      carpetaId: '3',
      tipoDocumento: 'certificado',
      fechaSubida: d(200),
      fechaVencimiento: d(15),
      version: 1,
      contentType: 'application/pdf',
      tamanoBytes: 120000,
      observaciones: 'Vencido, renovar',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '4',
      nombre: 'Licencia municipal',
      nombreArchivo: 'licencia-municipal.pdf',
      carpetaId: '5',
      tipoDocumento: 'licencia',
      fechaSubida: d(100),
      fechaVencimiento: next(25),
      version: 1,
      contentType: 'application/pdf',
      tamanoBytes: 95000,
      observaciones: 'Por vencer',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class DocumentalService {
  private carpetas: Carpeta[] = CARPETAS_INICIALES.map((c) => ({ ...c }));
  private documentos: Documento[] = demoDocumentos();
  private previewUrlCache: Record<string, string> = {};

  constructor(
    private http: HttpClient,
    private demoStorage: DemoStorageService
  ) {}

  /** En modo demo: carga desde IndexedDB (llamado por APP_INITIALIZER). */
  async loadFromDemoStorage(): Promise<void> {
    if (!this.isDemo) return;
    const [c, d] = await Promise.all([
      this.demoStorage.get<Carpeta[]>(KEY_CARPETAS),
      this.demoStorage.get<Documento[]>(KEY_DOCUMENTOS),
    ]);
    if (Array.isArray(c) && c.some((x) => x.id === 'root')) this.carpetas = c;
    if (Array.isArray(d)) this.documentos = d;
    await this.persistDemo();
  }

  /** En modo demo: persiste en IndexedDB. */
  private saveToStorage(): void {
    if (!this.isDemo) return;
    this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!this.isDemo) return Promise.resolve();
    return Promise.all([
      this.demoStorage.set(KEY_CARPETAS, this.carpetas),
      this.demoStorage.set(KEY_DOCUMENTOS, this.documentos),
    ]).then(() => {}).catch(() => {});
  }

  get isDemo(): boolean {
    return !!environment.demo;
  }

  getCarpetas(): Carpeta[] {
    return this.carpetas.map((c) => ({ ...c }));
  }

  /** Carpetas en orden para árbol (raíz primero, luego por orden). */
  getCarpetasArbol(): Carpeta[] {
    const list = this.getCarpetas();
    const byPadre = new Map<string | null, Carpeta[]>();
    list.forEach((c) => {
      const key = c.padreId ?? null;
      if (!byPadre.has(key)) byPadre.set(key, []);
      byPadre.get(key)!.push(c);
    });
    byPadre.forEach((arr) => arr.sort((a, b) => a.orden - b.orden));
    const out: Carpeta[] = [];
    function add(padreId: string | null) {
      const hijos = byPadre.get(padreId) ?? [];
      hijos.forEach((c) => {
        out.push(c);
        if (c.id !== 'root') add(c.id);
      });
    }
    add(null);
    return out;
  }

  getDocumentos(filters?: {
    carpetaId?: string;
    busqueda?: string;
    tipoDocumento?: TipoDocumento;
  }): Documento[] {
    let list = [...this.documentos];
    if (filters?.carpetaId) {
      const incluirHijos = (id: string): string[] => {
        const hijos = this.carpetas.filter((c) => c.padreId === id);
        return [id, ...hijos.flatMap((h) => incluirHijos(h.id))];
      };
      const ids = incluirHijos(filters.carpetaId);
      list = list.filter((d) => ids.includes(d.carpetaId));
    }
    if (filters?.busqueda?.trim()) {
      const q = filters.busqueda.trim().toLowerCase();
      list = list.filter(
        (d) =>
          d.nombre.toLowerCase().includes(q) ||
          d.nombreArchivo.toLowerCase().includes(q) ||
          (d.observaciones && d.observaciones.toLowerCase().includes(q))
      );
    }
    if (filters?.tipoDocumento) {
      list = list.filter((d) => d.tipoDocumento === filters.tipoDocumento);
    }
    return list
      .sort((a, b) => b.fechaSubida.localeCompare(a.fechaSubida))
      .map((d) => ({ ...d, contenidoBase64: undefined }));
  }

  getDocumentoById(id: string): Documento | null {
    const d = this.documentos.find((x) => x.id === id);
    return d ? { ...d } : null;
  }

  getResumenVencimientos(): { vencidos: number; porVencer: number } {
    let vencidos = 0;
    let porVencer = 0;
    this.documentos.forEach((d) => {
      if (isDocumentoVencido(d)) vencidos++;
      else if (isDocumentoPorVencer(d)) porVencer++;
    });
    return { vencidos, porVencer };
  }

  /** True si la carpeta no tiene documentos ni subcarpetas. */
  isCarpetaVacia(carpetaId: string): boolean {
    const tieneDocumentos = this.documentos.some((d) => d.carpetaId === carpetaId);
    if (tieneDocumentos) return false;
    const tieneSubcarpetas = this.carpetas.some((c) => c.padreId === carpetaId);
    return !tieneSubcarpetas;
  }

  createCarpeta(nombre: string, padreId: string | null): Carpeta {
    const id = 'c' + Date.now();
    const padre = padreId === null || padreId === 'root' ? 'root' : padreId;
    const hermanos = this.carpetas.filter((c) => c.padreId === padre);
    const orden = hermanos.length;
    const nueva: Carpeta = { id, nombre, padreId: padre, orden };
    this.carpetas.push(nueva);
    this.saveToStorage();
    return { ...nueva };
  }

  deleteCarpeta(id: string): boolean {
    if (id === 'root') return false;
    const hijos = this.carpetas.filter((c) => c.padreId === id);
    if (hijos.length > 0) return false;
    const docs = this.documentos.some((d) => d.carpetaId === id);
    if (docs) return false;
    const idx = this.carpetas.findIndex((c) => c.id === id);
    if (idx === -1) return false;
    this.carpetas.splice(idx, 1);
    this.saveToStorage();
    return true;
  }

  /** Sube un archivo. En demo guarda contenido en memoria (base64); si no es demo, llama al backend. */
  async upload(
    carpetaId: string,
    file: File,
    tipoDocumento: TipoDocumento,
    fechaVencimiento: string,
    observaciones: string
  ): Promise<Documento> {
    if (this.isDemo) {
      const contenidoBase64 = await this.fileToBase64(file);
      const id = String(Date.now());
      const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
      const doc: Documento = {
        id,
        nombre: file.name.replace(/\.[^.]+$/, '') || file.name,
        nombreArchivo: file.name,
        carpetaId,
        tipoDocumento,
        fechaSubida: new Date().toISOString().slice(0, 10),
        fechaVencimiento: fechaVencimiento || '',
        version: 1,
        contentType: file.type || 'application/octet-stream',
        tamanoBytes: file.size,
        contenidoBase64,
        observaciones: observaciones || '',
        createdAt: now,
        updatedAt: now,
      };
      this.documentos.push(doc);
      this.saveToStorage();
      return { ...doc, contenidoBase64: undefined };
    }
    return this.uploadBackend(carpetaId, file, tipoDocumento, fechaVencimiento, observaciones);
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private async uploadBackend(
    carpetaId: string,
    file: File,
    tipoDocumento: TipoDocumento,
    fechaVencimiento: string,
    observaciones: string
  ): Promise<Documento> {
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('carpetaId', carpetaId);
    formData.append('tipoDocumento', tipoDocumento);
    if (fechaVencimiento) formData.append('fechaVencimiento', fechaVencimiento);
    if (observaciones) formData.append('observaciones', observaciones);
    const apiUrl = (environment as { apiUrl?: string }).apiUrl || '';
    if (!apiUrl) {
      throw new Error('API no configurada. Configure environment.apiUrl para modo no demo.');
    }
    const res = await this.http
      .post<Documento>(`${apiUrl}/api/documental/upload`, formData)
      .toPromise();
    if (!res) throw new Error('Error al subir documento');
    return res;
  }

  /** URL para vista previa. En demo genera blob desde base64; en backend devuelve la URL del documento. */
  getPreviewUrl(doc: Documento): string | null {
    if (this.previewUrlCache[doc.id]) {
      return this.previewUrlCache[doc.id];
    }
    if (this.isDemo && doc.contenidoBase64) {
      try {
        const binary = atob(doc.contenidoBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: doc.contentType });
        const url = URL.createObjectURL(blob);
        this.previewUrlCache[doc.id] = url;
        return url;
      } catch {
        return null;
      }
    }
    if (doc.url) return doc.url;
    return null;
  }

  revokePreviewUrl(docId: string): void {
    if (this.previewUrlCache[docId]) {
      URL.revokeObjectURL(this.previewUrlCache[docId]);
      delete this.previewUrlCache[docId];
    }
  }

  deleteDocumento(id: string): boolean {
    this.revokePreviewUrl(id);
    const idx = this.documentos.findIndex((d) => d.id === id);
    if (idx === -1) return false;
    this.documentos.splice(idx, 1);
    this.saveToStorage();
    return true;
  }

  /** Nueva versión: en demo duplica documento con nuevo archivo y version+1. */
  async nuevaVersion(
    documentoId: string,
    file: File,
    fechaVencimiento: string,
    observaciones: string
  ): Promise<Documento | null> {
    const actual = this.documentos.find((d) => d.id === documentoId);
    if (!actual) return null;
    if (this.isDemo) {
      const contenidoBase64 = await this.fileToBase64(file);
      const id = String(Date.now());
      const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
      const doc: Documento = {
        ...actual,
        id,
        nombre: actual.nombre,
        nombreArchivo: file.name,
        version: actual.version + 1,
        fechaSubida: new Date().toISOString().slice(0, 10),
        fechaVencimiento: fechaVencimiento || actual.fechaVencimiento,
        tamanoBytes: file.size,
        contentType: file.type || actual.contentType,
        contenidoBase64,
        observaciones: observaciones || actual.observaciones,
        createdAt: now,
        updatedAt: now,
      };
      this.documentos.push(doc);
      this.saveToStorage();
      return { ...doc, contenidoBase64: undefined };
    }
    return this.uploadBackend(
      actual.carpetaId,
      file,
      actual.tipoDocumento,
      fechaVencimiento,
      observaciones
    ).then((d) => (d ? { ...d, version: actual.version + 1 } : null));
  }
}
